import React, { useEffect, useState } from "react";
import { useTranslation, withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { Sidebar } from "primereact/sidebar";
import { get, isEmpty } from "lodash";
import { request } from "../../../../../utils/Request";
import GroupedOrdersByRoute from "./GroupedOrdersByRoute";
import { Button } from "primereact/button";
import Location from "../../../../../components/Common/Display/Location";
import { utcDateToLocalDate } from "../../../../../utils/Helper";
import Tooltip from "../../../../../components/Common/Display/Tooltip";

interface IProps {
    id: string | null; // groupToken or load GUID
    mode?: 'group' | 'load' | 'inbound';
    onClose?: any | null;
    toastify?: any | null;
}

function AlpOutboundLoadGroupGenerateCmr(props: IProps) {
    const { t } = useTranslation();
    const { toastify } = props;
    const mode = props.mode ?? 'group';
    const providerApiPrefix = "/suppliers/provider";

    const [entity, setEntity] = useState<any>(null);
    const [panelState, setPanelState] = useState<boolean>(false);
    const [sourceType, setSourceType] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [generatingAndSending, setGeneratingAndSending] = useState(false);
    const [cmrsLoading, setCmrsLoading] = useState(false);
    const [cmrs, setCmrs] = useState<any[]>([]);
    const [previousCmrs, setPreviousCmrs] = useState<any[]>([]);
    const [sendingId, setSendingId] = useState<number | null>(null);

    const getApiErrorMessage = (error: any, fallback: string) => {
        if ( error?.status === 422 ) {
            return (
                get(error, ['data', 'message']) ||
                get(error, ['data', 'errors', 'document', 0]) ||
                get(error, ['data', 'errors', 'group_token', 0]) ||
                get(error, ['data', 'errors', 'cmr', 0]) ||
                get(error, ['data', 'errors', 'acm_connection', 0]) ||
                get(error, ['data', 'errors', 'supplier_compound', 0]) ||
                t("unprocessable entity")
            );
        }

        if ( error?.status === 403 ) {
            return t("action not allowed: 403");
        }

        if ( error?.status === 404 ) {
            return t("action not allowed: 404");
        }

        return fallback;
    };

    const onClose = () => {
        setPanelState(false);
        if ( props.onClose ) {
            props.onClose();
        }
    };

    const getDetailUrl = (id: string) => {
        if ( mode === 'inbound' ) {
            return `${ providerApiPrefix }/inbound/loads/${ id }`;
        }

        return mode === 'load'
            ? `${ providerApiPrefix }/outbound/loads/${ id }`
            : `${ providerApiPrefix }/aviso/outbound/groups/${ id }`;
    };

    const getCmrsUrl = (id: string) => {
        if ( mode === 'inbound' ) {
            return `${ providerApiPrefix }/inbound/loads/${ id }/cmrs`;
        }

        return mode === 'load'
            ? `${ providerApiPrefix }/outbound/loads/${ id }/cmrs`
            : `${ providerApiPrefix }/aviso/outbound/loads/${ id }/cmrs`;
    };

    const getCreateCmrUrl = (id: string) => {
        if ( mode === 'inbound' ) {
            return `${ providerApiPrefix }/inbound/loads/${ id }/cmr`;
        }

        return mode === 'load'
            ? `${ providerApiPrefix }/outbound/loads/${ id }/cmr`
            : `${ providerApiPrefix }/aviso/outbound/loads/${ id }/cmr`;
    };

    const getGenerateAndSendCmrUrl = (id: string) => {
        if ( mode === 'inbound' ) {
            return `${ providerApiPrefix }/inbound/loads/${ id }/cmr/generate-and-send`;
        }

        return mode === 'load'
            ? `${ providerApiPrefix }/outbound/loads/${ id }/cmr/generate-and-send`
            : `${ providerApiPrefix }/aviso/outbound/loads/${ id }/cmr/generate-and-send`;
    };

    const getSendCmrUrl = (id: string, cmrRowId: number) => {
        if ( mode === 'inbound' ) {
            return `${ providerApiPrefix }/inbound/loads/${ id }/cmrs/${ cmrRowId }/send`;
        }

        return mode === 'load'
            ? `${ providerApiPrefix }/outbound/loads/${ id }/cmrs/${ cmrRowId }/send`
            : `${ providerApiPrefix }/aviso/outbound/loads/${ id }/cmrs/${ cmrRowId }/send`;
    };

    const resolveCompoundStatus = (row?: any) => {
        if ( mode === 'group' ) {
            return get(row, ['cmr_compound_status'], null);
        }

        return get(entity, ['cmr_compound_status'], null);
    };

    const canSendCmr = (row?: any) => {
        return !!get(resolveCompoundStatus(row), ['can_send_cmr'], false);
    };

    const canGenerateAndSendCmr = () => {
        if ( mode === 'group' ) {
            const groupLoads = Array.isArray(entity?.alp_outbound_loads) ? entity.alp_outbound_loads : [];

            return groupLoads.some((load: any) => canSendCmr(load));
        }

        return canSendCmr();
    };

    const renderSendStatusIndicator = (status: any) => {
        if ( !status ) {
            return null;
        }

        if ( get(status, ['can_send_cmr']) ) {
            return (
                <p className="mb-0 small text-success">
                    <i className="fas fa-check-circle me-1" />
                    { t("cmr send ready") }
                </p>
            );
        }

        const message = get(status, ['message']);
        if ( message ) {
            return (
                <p className="mb-0 small text-warning">
                    <i className="fas fa-exclamation-triangle me-1" />
                    { message }
                </p>
            );
        }

        if ( !get(status, ['has_acm_connection']) ) {
            return (
                <p className="mb-0 small text-warning">
                    <i className="fas fa-exclamation-triangle me-1" />
                    { t("acm connection not configured") }
                </p>
            );
        }

        if ( !get(status, ['loads_synced']) ) {
            return (
                <p className="mb-0 small text-warning">
                    <i className="fas fa-exclamation-triangle me-1" />
                    { t("load not synced for cmr send") }
                </p>
            );
        }

        if ( !get(status, ['orders_synced']) ) {
            return (
                <p className="mb-0 small text-warning">
                    <i className="fas fa-exclamation-triangle me-1" />
                    { t("load orders not synced for cmr send") }
                </p>
            );
        }

        return (
            <p className="mb-0 small text-warning">
                <i className="fas fa-exclamation-triangle me-1" />
                { t("cmr send not ready") }
            </p>
        );
    };

    const getDetail = async (id: string) => {
        setLoading(true);
        try {
            const response = await request({
                url: getDetailUrl(id),
            });
            const data = response?.data?.data ?? response?.data;
            setEntity(data);
            const firstLoadSourceType = (mode === 'load' || mode === 'inbound')
                ? get(data, ['source_type'], null)
                : get(data, ['alp_outbound_loads', '0', 'source_type'], null);
            setSourceType(firstLoadSourceType ? `${ firstLoadSourceType }` : null);
            setPanelState(true);
        } catch (error: any) {
            toastify && toastify(getApiErrorMessage(error, t("server error")), "error");
        } finally {
            setLoading(false);
        }
    };

    const getCmrs = async (id: string) => {
        setCmrsLoading(true);
        try {
            const response = await request({
                url: getCmrsUrl(id),
            });
            const data = response?.data?.data ?? response?.data;
            // Newer backend returns { current, previous }; keep array fallback for older responses.
            if ( Array.isArray(data) ) {
                setCmrs(data);
                setPreviousCmrs([]);
                return;
            }

            setCmrs(Array.isArray(data?.current) ? data.current : []);
            setPreviousCmrs(Array.isArray(data?.previous) ? data.previous : []);
        } catch (error: any) {
            // List is secondary; keep error handling soft.
            if ( error?.status === 403 || error?.status === 404 ) {
                // ignore
            } else {
                // ignore
            }
        } finally {
            setCmrsLoading(false);
        }
    };

    useEffect(() => {
        setEntity(null);
        setPanelState(false);
        setSourceType(null);
        setCmrs([]);
        setPreviousCmrs([]);
        if ( !isEmpty(props.id) ) {
            // @ts-ignore
            getDetail(props.id);
            // @ts-ignore
            getCmrs(props.id);
        }
    }, [props.id, mode]);

    const loads = (mode === 'load' || mode === 'inbound')
        ? (entity ? [entity] : [])
        : (Array.isArray(entity?.alp_outbound_loads) ? entity.alp_outbound_loads : []);

    const headerReference = (mode === 'load' || mode === 'inbound')
        ? (get(entity, ['load_number']) || props.id)
        : props.id;

    const renderCmrTable = (rows: any[], allowSend: boolean = true) => (
        <div className={ 'table-responsive' }>
            <table className={ "fl-table" }>
                <thead>
                    <tr className="text-muted small">
                        <th>{ t("action") }</th>
                        <th>{ t("cmr number") }</th>
                        <th>
                            { mode === 'inbound' ? t("supplier compound") : t("dealer") }
                        </th>
                        <th>{ t("from") }</th>
                        <th>{ t("to") }</th>
                        <th>{ t("generated by") }</th>
                        <th>{ t("sent at") }</th>
                        { !allowSend && <th>{ t("replaced at") }</th> }
                    </tr>
                </thead>
                <tbody>
                    { rows.map((row: any) => (
                        <tr key={ row?.id }>
                            <td>
                                {
                                    (row.document && row.document.id) &&
                                  <>
                                      { allowSend && canSendCmr(row) && (
                                          <Tooltip tooltip={ t("send cmr to acm") } placeholder="left">
                                              <a
                                                className={ `me-2 ${ sendingId === row?.id ? 'disabled text-muted' : '' }` }
                                                href="#"
                                                onClick={ (e) => {
                                                    e.preventDefault();
                                                    onSendCmr(row);
                                                } }
                                              >
                                                  <i className={ 'fas fa-paper-plane' } />
                                              </a>
                                          </Tooltip>
                                      ) }

                                      <Tooltip tooltip={ t("download cmr") } placeholder="left">
                                          <a
                                            className={ 'me-2' }
                                            href={ `${ process.env.REACT_APP_DOCUMENT_URL }/${ row.document.id }` }
                                            target="_blank"
                                            rel="noreferrer"
                                          >
                                              <i className={ 'fas fa-download' } />
                                          </a>
                                      </Tooltip>
                                  </>
                                }
                            </td>
                            <td className="small">
                                { get(row, ['cmr_number'], "-") }
                            </td>
                            <td className="small">
                                { mode === 'inbound'
                                    ? (get(row, ['supplier_compound']) || get(row, ['dealer_name'], "-"))
                                    : get(row, ['dealer_name'], "-") }
                            </td>
                            <td className="small">
                                <Location
                                    item={ get(row, ['from_location']) }
                                    titled={ 1 } />
                            </td>
                            <td className="small">
                                <Location
                                    item={ get(row, ['to_location']) }
                                    titled={ 1 } />
                            </td>
                            <td className="small">
                                { get(row, ['author', "name"], "-") }
                                <div>{ utcDateToLocalDate(row.created_at) }</div>
                            </td>
                            <td className="small">
                                <div>{ utcDateToLocalDate(row.sent_at) }</div>
                            </td>
                            { !allowSend && (
                                <td className="small">
                                    <div>{ utcDateToLocalDate(row.deleted_at) }</div>
                                </td>
                            ) }
                        </tr>
                    )) }
                </tbody>
            </table>
        </div>
    );

    const onCreateCmr = async (): Promise<void> => {
        if ( isEmpty(props.id) ) {
            toastify && toastify(t("invalid data"), "error");
            return;
        }

        const id = `${ props.id }`;
        setCreating(true);
        try {
            const response = await request({
                method: "POST",
                url: getCreateCmrUrl(id),
                data: {},
            });

            // For now we just show a success toast. The response payload will be used later.
            const _data = response?.data?.data ?? response?.data ?? null;
            setEntity((prev: any) => ( {
                ...( prev ?? {} ),
                cmr_payload: _data,
            } ));
            toastify && toastify(t("cmr request created"), "success");

            // Refresh list of generated CMRs for this group
            getCmrs(id);
        } catch (error: any) {
            toastify && toastify(getApiErrorMessage(error, t("server error")), "error");
        } finally {
            setCreating(false);
        }
    };

    const onGenerateAndSendCmr = async (): Promise<void> => {
        if ( isEmpty(props.id) ) {
            toastify && toastify(t("invalid data"), "error");
            return;
        }

        if ( !canGenerateAndSendCmr() ) {
            toastify && toastify(t("cmr send not ready"), "error");
            return;
        }

        const id = `${ props.id }`;
        setGeneratingAndSending(true);
        try {
            const response = await request({
                method: "POST",
                url: getGenerateAndSendCmrUrl(id),
                data: {},
            });

            const data = response?.data?.data ?? response?.data ?? {};
            const generated = Number(data?.generated ?? 0);
            const sent = Number(data?.sent ?? 0);

            if ( data?.success && sent > 0 ) {
                if ( generated > sent ) {
                    toastify && toastify(
                        t("cmr generated partial send", { sent, generated }),
                        "success"
                    );
                } else {
                    toastify && toastify(t("cmr generated and sent"), "success");
                }

                getCmrs(id);
                return;
            }

            toastify && toastify(t("cmr generate and send failed"), "error");
        } catch (error: any) {
            toastify && toastify(getApiErrorMessage(error, t("cmr generate and send failed")), "error");
        } finally {
            setGeneratingAndSending(false);
        }
    };

    const onSendCmr = async (row: any): Promise<void> => {
        const cmrRowId = get(row, ['id']);

        if ( isEmpty(props.id) || !cmrRowId ) {
            toastify && toastify(t("invalid data"), "error");
            return;
        }

        if ( !canSendCmr(row) ) {
            toastify && toastify(t("cmr send not ready"), "error");
            return;
        }

        const id = `${ props.id }`;
        setSendingId(cmrRowId);
        try {
            const response = await request({
                method: "POST",
                url: getSendCmrUrl(id, cmrRowId),
                data: {},
            });
            const data = response?.data?.data ?? response?.data ?? {};

            if ( data?.success ) {
                const hooks = data?.hooks_shot ?? null;
                toastify && toastify(
                    hooks ? `${ t("cmr sent") } (${ hooks })` : t("cmr sent"),
                    "success"
                );

                getCmrs(id);
                return;
            }

            // If backend returns non-throwing failures (rare), show message if present.
            const msg = data?.message || t("cmr send failed");
            toastify && toastify(msg, "error");
        } catch (error: any) {
            toastify && toastify(getApiErrorMessage(error, t("cmr send failed")), "error");
        } finally {
            setSendingId(null);
        }
    };

    return (
        <Sidebar
            header={
                <div className="bg-body-tertiary card-header">
                    <h5 className={ "card-title mt-0" }>{ t("create cmr") }</h5>
                    <p className="mb-0">
                        <strong>{ mode === 'inbound'
                            ? t("inbound load") : (mode === 'load' ? t("outbound load") : t("group token")) }:</strong>{ " " }
                        { headerReference }
                    </p>
                    { sourceType && (
                        <p className="mb-0 text-capitalize">
                            <strong>{ t("provider") }:</strong>{ " " }
                            { sourceType === "vehicle_logistics" ? t("vehicle logistics") : t("emil frey") }
                        </p>
                    ) }
                    { mode === 'inbound' && !isEmpty(get(entity, ['supplier_compound'])) && (
                        <p className="mb-0 text-capitalize">
                            <strong>{ t("supplier compound") }:</strong>{ " " }
                            { get(entity, ['supplier_compound']) }
                        </p>
                    ) }
                    { (mode === 'load' || mode === 'inbound') && renderSendStatusIndicator(get(entity, ['cmr_compound_status'])) }
                    { mode === 'group' && Array.isArray(entity?.alp_outbound_loads) && entity.alp_outbound_loads.map((load: any) => (
                        <div key={ load?.id || load?.load_number }>
                            <p className="mb-0 small">
                                <strong>{ t("load number") }:</strong>{ " " }
                                { get(load, ['load_number'], "-") }
                            </p>
                            { renderSendStatusIndicator(get(load, ['cmr_compound_status'])) }
                        </div>
                    )) }
                </div>
            }
            visible={ panelState }
            position="right"
            onHide={ onClose }
            blockScroll={ true }
            style={ { width: "900px" } }
            closeOnEscape={ false }
        >
            <div
                style={ {
                    height: "3px",
                    background: "linear-gradient(90deg, var(--primary-color) 0%, rgba(33, 150, 243, 0) 50%)",
                } }
            />

            <div className="mt-4">
                { loading && (
                    <div className="text-muted small">
                        { t("loading") }...
                    </div>
                ) }

                { !loading && (
                    <>
                        <div className="d-flex align-items-center justify-content-end gap-2 mb-3">
                            <Button
                                size="small"
                                outlined
                                disabled={ creating || generatingAndSending }
                                onClick={ onCreateCmr }
                                tooltip={ t("create cmr") }
                                tooltipOptions={ { position: "top" } }
                            >
                                <i className="pi pi-file-pdf me-2" />
                                { creating ? t("loading") : t("create cmr") }
                            </Button>
                            <Button
                                size="small"
                                disabled={ creating || generatingAndSending || !canGenerateAndSendCmr() }
                                onClick={ onGenerateAndSendCmr }
                                tooltip={ t("generate and send cmr") }
                                tooltipOptions={ { position: "top" } }
                            >
                                <i className="pi pi-send me-2" />
                                { generatingAndSending ? t("loading") : t("generate and send cmr") }
                            </Button>
                        </div>

                        <h4 className="card-title mb-3">{ t("generated cmrs") }</h4>
                        { cmrsLoading && (
                            <div className="text-muted small mb-3">{ t("loading") }...</div>
                        ) }
                        { !cmrsLoading && cmrs.length === 0 && (
                            <div className="text-muted small mb-4">{ t("no cmrs generated") }</div>
                        ) }
                        { !cmrsLoading && cmrs.length > 0 && (
                            renderCmrTable(cmrs)
                        ) }

                        <h4 className="card-title mb-3">{ t("routes") }</h4>
                        <GroupedOrdersByRoute loads={ loads } t={ t } mode={ mode } />

                        <div className="mt-4">
                            <h4 className="card-title mb-3">{ t("previous cmrs") }</h4>
                            { cmrsLoading && (
                                <div className="text-muted small mb-3">{ t("loading") }...</div>
                            ) }
                            { !cmrsLoading && previousCmrs.length === 0 && (
                                <div className="text-muted small mb-4">{ t("no previous cmrs") }</div>
                            ) }
                            { !cmrsLoading && previousCmrs.length > 0 && renderCmrTable(previousCmrs, false) }
                        </div>
                    </>
                ) }
            </div>
        </Sidebar>
    );
}

AlpOutboundLoadGroupGenerateCmr.propTypes = {
    t: PropTypes.any,
};

export default withTranslation()(AlpOutboundLoadGroupGenerateCmr);
